import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAssets, useDeleteAsset } from '../lib/api/assets';
import { useCampaigns } from '../lib/api/campaigns';
import {
  ASSET_STATUSES,
  ASSET_TYPES_BY_CATEGORY,
  AssetStatus,
  AssetType,
  CampaignAsset,
} from '../lib/types';
import { Button } from '../components/ui/Button';
import { Icon, IconName } from '../components/ui/Icon';
import { Chip } from '../components/ui/Chip';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useToast } from '../components/ui/Toast';
import {
  parseDate,
  today,
  toISODate,
  fmtShort,
  daysFromToday,
} from '../lib/dates';
import CreateAssetModal from '../components/modals/CreateAssetModal';
import AssetGanttTimeline from '../components/gantt/AssetGanttTimeline';
import { assetDisplayTitle } from '../lib/assetTitle';
import { ASSET_TYPE_ICON } from '../lib/assetIcons';
import { dayCapacityIssues } from '../lib/homepageCapacity';

const HOMEPAGE_TYPES = ASSET_TYPES_BY_CATEGORY['Homepage'];

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

type ViewMode = 'calendar' | 'timeline';

const VIEWS: { key: ViewMode; label: string; icon: IconName }[] = [
  { key: 'calendar', label: 'Calendar', icon: 'calendar' },
  { key: 'timeline', label: 'Timeline', icon: 'trending' },
];

const heatmapBg = (count: number) => {
  if (count === 0) return 'rgba(232, 163, 62, 0.18)';
  if (count === 1) return 'rgba(43, 138, 90, 0.12)';
  if (count === 2) return 'rgba(43, 138, 90, 0.25)';
  return 'rgba(43, 138, 90, 0.45)';
};

// One icon per placement type — shared with the campaign page.
const TYPE_ICON = ASSET_TYPE_ICON;

const TYPE_COLOR: Record<string, string> = {
  'Hero Banner 100%': 'var(--seasonal)',
  'Hero Banner 50%': '#6BB88F',
  'Homepage Product Grid': 'var(--sitewide)',
  'Homepage Collection Cards': '#2B4138',
  'Homepage Promotional Banner': 'var(--brand)',
  'Homepage Product Carousel': 'var(--accent-3, #E8A33E)',
  'Homepage Browse by Room': 'var(--accent-6, #5B3DA6)',
  'Homepage Brand Logo Carousel': 'var(--accent-5, #C94B9A)',
  'Pop-ups & Banners': 'var(--brand)',
  'Navigation Bar': 'var(--ink-3)',
};

/** Real top-to-bottom order of the blocks on lamptwist.com's homepage —
 *  each calendar day cell lists its placements in this order, so a cell
 *  reads like a mini homepage and same-type placements sit together. */
const HOMEPAGE_LAYOUT_ORDER: string[] = [
  'Navigation Bar',
  'Hero Banner 100%',
  'Hero Banner 50%',
  'Homepage Product Grid',
  'Homepage Collection Cards',
  'Homepage Promotional Banner',
  'Homepage Product Carousel',
  'Homepage Browse by Room',
  'Homepage Brand Logo Carousel',
  'Pop-ups & Banners',
];
const layoutRank = (t: string) => {
  const i = HOMEPAGE_LAYOUT_ORDER.indexOf(t);
  return i === -1 ? HOMEPAGE_LAYOUT_ORDER.length : i;
};

const buildMonthGrid = (year: number, month: number) => {
  const first = new Date(year, month, 1);
  const dow = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - dow);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1];
    const d = new Date(last);
    d.setDate(last.getDate() + 1);
    cells.push(d);
  }
  return cells;
};

const Homepage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: assets = [], isLoading } = useAssets({ category: 'Homepage' });
  const { data: campaigns = [] } = useCampaigns();
  const deleteAsset = useDeleteAsset();
  const campaignById = new Map(campaigns.map((c) => [c.id, c]));

  const [editTarget, setEditTarget] = useState<CampaignAsset | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CampaignAsset | null>(null);

  const [cursor, setCursor] = useState<Date>(() => {
    const t = today();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  // Multiple types can be active at once; empty array = all types.
  const [typeFilter, setTypeFilter] = useState<AssetType[]>([]);
  const [statusFilter, setStatusFilter] = useState<AssetStatus | null>(null);
  const [heatmap, setHeatmap] = useState(true);
  const [view, setView] = useState<ViewMode>('calendar');
  const [createOpen, setCreateOpen] = useState(false);
  const [createDate, setCreateDate] = useState<string | undefined>();

  const filtered = useMemo(
    () =>
      assets.filter((a) => {
        if (typeFilter.length > 0 && !typeFilter.includes(a.asset_type)) return false;
        if (statusFilter && a.status !== statusFilter) return false;
        return true;
      }),
    [assets, typeFilter, statusFilter],
  );

  // Undated assets ("To plan") don't appear on the calendar.
  const datedFiltered = useMemo(
    () => filtered.filter((a): a is CampaignAsset & { publish_date: string } => !!a.publish_date),
    [filtered],
  );

  /** Each asset can span multiple days. Index every (date, asset) pair. */
  const assetsByDate = useMemo(() => {
    const map = new Map<string, CampaignAsset[]>();
    for (const a of datedFiltered) {
      const start = parseDate(a.publish_date);
      const end = a.end_date ? parseDate(a.end_date) : start;
      const cursor2 = new Date(start);
      while (cursor2 <= end) {
        const iso = toISODate(cursor2);
        const arr = map.get(iso) ?? [];
        arr.push(a);
        map.set(iso, arr);
        cursor2.setDate(cursor2.getDate() + 1);
      }
    }
    // Within a day, mirror the homepage's own top-to-bottom block order;
    // same-type placements group together, tie-break on campaign name.
    for (const arr of map.values()) {
      arr.sort(
        (a, b) =>
          layoutRank(a.asset_type) - layoutRank(b.asset_type)
          || (a.campaign_name ?? '').localeCompare(b.campaign_name ?? ''),
      );
    }
    return map;
  }, [filtered]);

  const grid = useMemo(() => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  /** Count distinct assets that have at least one live day in each week.
   *  Multi-day banners count once per week they cover, so we never flag a
   *  week as "Gap" if a long-running asset is actually on the homepage. */
  const weekCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (let i = 0; i < grid.length; i += 7) {
      const seen = new Set<number>();
      for (let j = 0; j < 7; j++) {
        const d = grid[i + j];
        const cell = assetsByDate.get(toISODate(d)) ?? [];
        for (const a of cell) seen.add(a.id);
      }
      counts.set(i / 7, seen.size);
    }
    return counts;
  }, [grid, assetsByDate]);

  const gaps = useMemo(() => {
    if (!heatmap) return [] as { weekStart: Date; weekEnd: Date }[];
    const out: { weekStart: Date; weekEnd: Date }[] = [];
    for (let i = 0; i < grid.length; i += 7) {
      if ((weekCounts.get(i / 7) ?? 0) !== 0) continue;
      const s = grid[i];
      const e = grid[i + 6];
      if (e.getMonth() !== cursor.getMonth() && s.getMonth() !== cursor.getMonth()) continue;
      out.push({ weekStart: s, weekEnd: e });
    }
    return out;
  }, [heatmap, grid, weekCounts, cursor]);

  const todayDate = today();
  const todayISO = toISODate(todayDate);
  const monthLabel = cursor.toLocaleDateString('en', { month: 'long', year: 'numeric' });

  /** "Live today" strip: assets whose date range covers today, regardless of category-internal type. */
  const liveToday = useMemo(() => assetsByDate.get(todayISO) ?? [], [assetsByDate, todayISO]);
  const liveByType = useMemo(() => {
    const map = new Map<AssetType, CampaignAsset[]>();
    for (const a of liveToday) {
      const arr = map.get(a.asset_type) ?? [];
      arr.push(a);
      map.set(a.asset_type, arr);
    }
    return map;
  }, [liveToday]);

  const upcoming = useMemo(
    () =>
      datedFiltered
        .filter((a) => {
          const d = daysFromToday(a.publish_date);
          return d >= 0 && d <= 30;
        })
        .sort((a, b) => a.publish_date.localeCompare(b.publish_date)),
    [datedFiltered],
  );

  const openCreate = (date?: string) => {
    setCreateDate(date);
    setCreateOpen(true);
  };

  const fmtRange = (a: CampaignAsset) =>
    !a.publish_date
      ? 'To plan'
      : a.end_date && a.end_date !== a.publish_date
      ? `${fmtShort(a.publish_date)} → ${fmtShort(a.end_date)}`
      : fmtShort(a.publish_date);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Planning</div>
          <h1 className="page-title">Homepage</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {view === 'calendar' && (
            <Button
              variant={heatmap ? 'primary' : 'ghost'}
              onClick={() => setHeatmap((v) => !v)}
            >
              <Icon name="sparkle" size={13} /> Heatmap {heatmap ? 'on' : 'off'}
            </Button>
          )}
          <Button variant="primary" onClick={() => openCreate()}>
            <Icon name="plus" size={13} /> New homepage asset
          </Button>
        </div>
      </div>

      {/* Live today strip */}
      <div
        className="card"
        style={{
          padding: '14px 16px',
          marginBottom: 18,
          background: 'var(--bg-elevated)',
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--ink-4)',
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          Live on the homepage today
        </div>
        {liveToday.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--ink-4)' }}>
            Nothing scheduled to be live on the homepage today.
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {HOMEPAGE_TYPES.map((t) => {
              const live = liveByType.get(t) ?? [];
              if (live.length === 0) return null;
              return (
                <div key={t} style={{ flex: '1 1 200px', minWidth: 200 }}>
                  <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: TYPE_COLOR[t] || 'var(--ink-4)', display: 'inline-flex' }}>
                      <Icon name={TYPE_ICON[t]} size={12} />
                    </span>
                    {t}
                  </div>
                  {live.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setEditTarget(a)}
                      style={{
                        background: 'var(--bg-subtle)',
                        borderRadius: 'var(--r-md)',
                        padding: '6px 8px',
                        marginBottom: 4,
                        width: '100%',
                        textAlign: 'left',
                        fontSize: 12.5,
                        cursor: 'pointer',
                      }}
                    >
                      {assetDisplayTitle(a, { withCampaign: true })}
                      <div style={{ fontSize: 10.5, color: 'var(--ink-4)', marginTop: 1 }}>
                        until {fmtShort((a.end_date ?? a.publish_date) as string)} · {a.status}
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="filter-bar" style={{ marginBottom: 6 }}>
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            className={`filter-pill ${view === v.key ? 'active' : ''}`}
            onClick={() => setView(v.key)}
          >
            <Icon name={v.icon} size={12} />
            {v.label}
          </button>
        ))}
      </div>

      {view === 'calendar' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, marginBottom: 10 }}>
          <button type="button" className="icon-btn" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
            <Icon name="chevronLeft" size={14} />
          </button>
          <div className="serif" style={{ fontSize: 24 }}>{monthLabel}</div>
          <button type="button" className="icon-btn" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
            <Icon name="chevronRight" size={14} />
          </button>
          <Button variant="ghost" onClick={() => setCursor(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1))}>
            Today
          </Button>
        </div>
      )}

      <div className="filter-bar">
        <button type="button" className={`filter-pill ${typeFilter.length === 0 ? 'active' : ''}`} onClick={() => setTypeFilter([])}>
          All types
        </button>
        {HOMEPAGE_TYPES.map((t) => (
          <button
            key={t}
            type="button"
            className={`filter-pill ${typeFilter.includes(t) ? 'active' : ''}`}
            onClick={() => setTypeFilter((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]))}
          >
            <span className="dot" style={{ background: TYPE_COLOR[t] }} />
            {t.replace('Homepage ', '')}
          </button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 6px' }} />
        <button type="button" className={`filter-pill ${statusFilter === null ? 'active' : ''}`} onClick={() => setStatusFilter(null)}>
          All statuses
        </button>
        {ASSET_STATUSES.map((s) => (
          <button key={s} type="button" className={`filter-pill ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
            {s}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>Loading…</div>
      ) : view === 'timeline' ? (
        filtered.length === 0 ? (
          <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
            No homepage assets match your filters.
          </div>
        ) : (
          <AssetGanttTimeline
            assets={filtered}
            onAssetClick={(a) => setEditTarget(a)}
            titleFor={(a) => assetDisplayTitle(a, { withCampaign: true })}
            colorFor={(a) => TYPE_COLOR[a.asset_type] || 'var(--ink-3)'}
            labelHeader="Homepage placement"
            rangeWeeks={16}
            startOffsetWeeks={-2}
          />
        )
      ) : (
        <div className="cal">
          {DOW.map((d) => <div key={d} className="cal-dow">{d}</div>)}
          {grid.map((d, i) => {
            const iso = toISODate(d);
            const outside = d.getMonth() !== cursor.getMonth();
            const isToday = todayISO === iso;
            const cellAssets = assetsByDate.get(iso) ?? [];
            const weekIndex = Math.floor(i / 7);
            const weekCount = weekCounts.get(weekIndex) ?? 0;
            const isSunday = i % 7 === 6;
            const issues = dayCapacityIssues(cellAssets);
            const hardIssue = issues.some((x) => x.severity === 'hard');
            const hasIssue = issues.length > 0;
            return (
              <div
                key={iso}
                className={`cal-cell ${outside ? 'outside' : ''} ${isToday ? 'today' : ''}`}
                onClick={() => openCreate(iso)}
                style={{
                  cursor: 'pointer',
                  background: heatmap && !outside ? heatmapBg(weekCount) : undefined,
                  outline: hasIssue && !outside
                    ? `1.5px solid ${hardIssue ? 'var(--warn)' : '#c99a1e'}`
                    : undefined,
                  outlineOffset: hasIssue && !outside ? -1 : undefined,
                }}
                title={hasIssue ? issues.map((x) => x.message).join('\n') : undefined}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="cal-date">{d.getDate()}</div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {hasIssue && !outside && (
                      <span
                        style={{
                          fontSize: 9.5,
                          padding: '1px 5px',
                          borderRadius: 3,
                          background: hardIssue ? 'var(--warn-bg)' : '#fff7e0',
                          color: hardIssue ? 'var(--warn)' : '#7a5300',
                          fontWeight: 600,
                        }}
                      >
                        {hardIssue ? 'conflict' : 'busy'}
                      </span>
                    )}
                    {heatmap && isSunday && !outside && (
                      <div style={{ fontSize: 10, color: 'var(--ink-4)', fontWeight: 500 }}>
                        {weekCount === 0 ? 'Gap' : `${weekCount}/wk`}
                      </div>
                    )}
                  </div>
                </div>
                {cellAssets.map((a) => (
                  <div
                    key={`${a.id}-${iso}`}
                    className="cal-post"
                    style={{
                      borderLeftColor: TYPE_COLOR[a.asset_type] || 'var(--ink-5)',
                      // Light tint of the type colour so the whole chip reads as
                      // its placement type, not just the 2px bar.
                      background: `color-mix(in srgb, ${TYPE_COLOR[a.asset_type] || 'var(--ink-5)'} 10%, var(--bg-elevated))`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditTarget(a);
                    }}
                    title={assetDisplayTitle(a, { withCampaign: true })}
                  >
                    <span style={{ color: TYPE_COLOR[a.asset_type] || 'var(--ink-4)', flexShrink: 0, display: 'inline-flex', marginTop: 1 }}>
                      <Icon name={TYPE_ICON[a.asset_type]} size={12} />
                    </span>
                    <span className="cal-post-caption">{assetDisplayTitle(a, { withCampaign: true })}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {view === 'calendar' && heatmap && gaps.length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <div className="section-title">Gap weeks</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                {gaps.length} empty week{gaps.length === 1 ? '' : 's'} visible
              </div>
            </div>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
            {gaps.map((g, i) => (
              <div
                key={i}
                style={{
                  border: '1px dashed var(--status-review)',
                  background: 'rgba(251, 239, 212, 0.6)',
                  borderRadius: 'var(--r-md)',
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--status-review)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 6 }}>
                  Week of {fmtShort(g.weekStart)} — {fmtShort(g.weekEnd)}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 10 }}>
                  Nothing planned for the homepage. Add a banner or product grid.
                </div>
                <Button variant="ghost" onClick={() => openCreate(toISODate(g.weekStart))}>
                  <Icon name="plus" size={12} /> Plan an asset
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="section">
        <div className="section-head">
          <div>
            <div className="section-title">Next 30 days</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
              {upcoming.length} asset{upcoming.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>
        {upcoming.length === 0 ? (
          <div className="card" style={{ padding: 20, color: 'var(--ink-4)' }}>
            Nothing planned for the homepage in the next 30 days.
          </div>
        ) : (
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
            {upcoming.map((a) => {
              const c = a.campaign ? campaignById.get(a.campaign) : null;
              return (
                <div key={a.id} className="post-card" style={{ position: 'relative' }}>
                  <div onClick={() => setEditTarget(a)} style={{ cursor: 'pointer' }}>
                    <div className="post-card-header">
                      <span style={{ color: TYPE_COLOR[a.asset_type] || 'var(--ink-4)', display: 'inline-flex' }}>
                        <Icon name={TYPE_ICON[a.asset_type]} size={13} />
                      </span>
                      <span className="mono">{fmtRange(a)}</span>
                      <Chip kind="status" value={a.status} />
                    </div>
                    <div className="post-card-caption">{assetDisplayTitle(a)}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>
                      {c ? (
                        <button
                          type="button"
                          className="link"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/campaigns/${c.id}`);
                          }}
                        >
                          {c.name}
                        </button>
                      ) : a.brand_details && a.brand_details.length > 0 ? (
                        a.brand_details.map((b) => b.name).join(', ')
                      ) : 'Unassigned'} · {a.asset_type}
                    </div>
                  </div>
                  <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
                    <button
                      type="button"
                      className="icon-btn"
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(a);
                      }}
                      style={{ color: 'var(--warn)' }}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CreateAssetModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultDate={createDate}
        defaultCategory="Homepage"
        defaultType={typeFilter[0] ?? 'Hero Banner 100%'}
      />
      <CreateAssetModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        asset={editTarget}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await deleteAsset.mutateAsync(deleteTarget.id);
            toast.push('Placement deleted');
          } catch {
            toast.push('Could not delete placement');
          }
          setDeleteTarget(null);
        }}
        title={`Delete "${deleteTarget?.title ?? 'placement'}"?`}
        description="This will permanently remove the homepage placement."
        confirmLabel="Delete"
        danger
        busy={deleteAsset.isPending}
      />
    </div>
  );
};

export default Homepage;
